import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import { adminApi } from '../service/adminApiService';
import { OperationCheckboxGroup } from '../components/OperationCheckboxGroup';
import { useToast } from '../components/ToastProvider';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';

export const TemplateFormPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { template_name: paramName } = useParams<{ template_name?: string }>();
  const isEdit = Boolean(paramName);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [operations, setOperations] = useState<string[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !paramName) return;
    let cancelled = false;
    (async () => {
      try {
        const existing = await adminApi.getTemplate(paramName);
        if (cancelled) return;
        setName(existing.template_name);
        setDescription(existing.description || '');
        setOperations(existing.operations);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load template');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, paramName]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isEdit && paramName) {
        await adminApi.updateTemplate(paramName, {
          description,
          operations,
        });
      } else {
        await adminApi.createTemplate({
          template_name: name,
          description,
          operations,
        });
      }
      toast.push({
        type: 'success',
        message: isEdit ? `Updated ${name}` : `Created ${name}`,
      });
      navigate('/admin/templates');
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Row alignmentHorizontal="center" spacingInset="500">
        <Loader type="circular" size="large" />
      </Row>
    );
  }

  return (
    <Column spacing="large">
      <PageHeader
        title={isEdit ? `Edit template: ${paramName}` : 'New template'}
        subtitle="Templates pre-fill the operations picker on the group form."
      />

      {error && <Alert type="error">{error}</Alert>}

      <Section>
      <Column spacing="medium" maxWidth={720}>

      <Column spacing="xsmall">
        <label htmlFor="template-name">Template name</label>
        <Input
          id="template-name"
          type="text"
          value={name}
          onChange={setName}
          placeholder="e.g. data-science-readonly"
          disabled={isEdit}
        />
      </Column>

      <Column spacing="xsmall">
        <label htmlFor="template-description">Description</label>
        <Input
          id="template-description"
          type="text"
          value={description}
          onChange={setDescription}
          placeholder="One-line description shown in the picker"
        />
      </Column>

      <Text type="h100">Operations</Text>
      <OperationCheckboxGroup value={operations} onChange={setOperations} />

      <Row alignmentHorizontal="end" spacing="small">
        <Button type="tertiary" onClick={() => navigate('/admin/templates')}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={save}
          disabled={!name || operations.length === 0 || saving}
        >
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
        </Button>
      </Row>
      </Column>
      </Section>
    </Column>
  );
};
